import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";

import Logo from "../../../components/Logo";
import Brand from "../../../components/Brand";
import brand from "../../../inc/branding.json";

const LAST_UPDATED = "February 17, 2026";

const Section = ({ title, children }) => (
  <Box sx={{ textAlign: "left" }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: "left" }}>
      {title}
    </Typography>
    <Box
      sx={{
        color: "text.secondary",
        "& p": { mb: 1.5, textAlign: "justify", textJustify: "inter-word" },
        "& ul": { mt: 0.5, mb: 1.5, pl: 2.5 },
        "& li": { textAlign: "left" },
      }}
    >
      {children}
    </Box>
  </Box>
);

const ToS = () => {
  const dba = brand.brandConfig?.config?.dba;
  const legalName = brand.brandConfig?.config?.name;
  const legalConfig = brand.brandConfig?.config?.legal || {};
  const legalEmail =
    brand.brandConfig?.config?.legal_email ||
    brand.brandConfig?.config?.support_email;
  const governingJurisdiction =
    legalConfig.governing_jurisdiction || "State of Delaware, United States";
  const venue =
    legalConfig.venue || "State or federal courts located in Delaware, United States";
  const arbitrationEnabled = Boolean(legalConfig.arbitration_enabled);
  const arbitrationForum = legalConfig.arbitration_forum || "a mutually agreed arbitration forum";
  const arbitrationRules = legalConfig.arbitration_rules || "the forum's applicable arbitration rules";
  const liabilityCapMonths = Number(legalConfig.liability_cap_months || 6);
  const address = brand.brandConfig?.config?.address || {};
  const legalAddress = [
    address.line1,
    address.line2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(" "),
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 6 }, bgcolor: "grey.50" }}>
      <Container maxWidth="md">
        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Logo size={34} />
              <Brand width={111} height={48} />
            </Stack>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Terms of Service
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last updated: {LAST_UPDATED}
              </Typography>
            </Box>

            <Section title="Important Notice">
              <p>
                These Terms of Service are a legal agreement between you and {legalName} ("Company", "we", "us", "our")
                governing your access to and use of the {dba} platform, website, applications, APIs, and related services
                (collectively, the "Services").
              </p>
              <p>
                This document is a platform-ready legal draft and should be reviewed by your legal counsel before production use.
              </p>
            </Section>

            <Section title="1. Acceptance of Terms">
              <p>
                By creating an account, accessing, or using the Services, you agree to be bound by these Terms and all policies
                incorporated by reference. If you do not agree, you must not access or use the Services.
              </p>
            </Section>

            <Section title="2. Definitions">
              <ul>
                <li>"Client" means a user account onboarding to request staffing/hiring services.</li>
                <li>"Contractor" means a user account onboarding to provide services and submit work records.</li>
                <li>"Admin" means an internal operations role authorized by the Company to manage workflow.</li>
                <li>"Hire Request" means a client-submitted request tracked through platform workflow statuses.</li>
                <li>"Content" means all data, text, files, profile details, logs, and materials submitted through the Services.</li>
              </ul>
            </Section>

            <Section title="3. Eligibility and Account Registration">
              <ul>
                <li>You must be at least 18 years old and legally able to enter contracts.</li>
                <li>You must provide accurate, complete, and current registration information.</li>
                <li>
                  You are responsible for all activity under your credentials and must promptly notify us of unauthorized use.
                </li>
              </ul>
            </Section>

            <Section title="4. Role Model and Account Restrictions">
              <ul>
                <li>Each account is assigned a single role (Client, Contractor, or Admin) and role switching may be restricted.</li>
                <li>Role assignment and access permissions are enforced by platform security controls.</li>
                <li>You must not attempt privilege escalation or bypass role-based controls.</li>
              </ul>
            </Section>

            <Section title="5. Security and Authentication">
              <ul>
                <li>We may require email verification, re-authentication, and multi-factor authentication.</li>
                <li>Session controls and trusted-device behavior may change to maintain security and compliance.</li>
                <li>You must maintain secure endpoint/device practices when using the Services.</li>
              </ul>
            </Section>

            <Section title="6. Services and Workflow">
              <p>
                The Services support operational workflows, including hire requests, candidate shortlisting/matching, contract
                status tracking, contractor timesheets, and invoice/payment operations.
              </p>
              <ul>
                <li>Workflow states and transitions are system-controlled and may be limited by role and policy.</li>
                <li>Company operations may claim, unclaim, re-open, expire, close, or otherwise manage requests.</li>
                <li>Contract-signing may occur via third-party systems outside the Services.</li>
              </ul>
            </Section>

            <Section title="7. Timesheets, Invoices, and Payments">
              <ul>
                <li>Contractors may submit work logs and timesheets subject to Company review and approval rules.</li>
                <li>Approved records may generate invoices and trigger payment processing workflows.</li>
                <li>Payment processing and payouts are provided by third-party processors, including Stripe.</li>
                <li>
                  You authorize us to share required account and transaction data with payment processors to perform Services.
                </li>
              </ul>
            </Section>

            <Section title="8. Fees, Pricing, and Taxes">
              <p>
                Pricing, platform fees, and operational margins may be configured by Company operations. Tax handling may vary
                by jurisdiction and your role. You are responsible for your own tax filings and compliance unless expressly
                stated otherwise in a written agreement.
              </p>
            </Section>

            <Section title="9. Acceptable Use and Prohibited Conduct">
              <ul>
                <li>No unlawful, deceptive, abusive, or fraudulent conduct.</li>
                <li>No malware, exploitation, security probing, scraping abuse, or service disruption.</li>
                <li>No false credentials, forged records, or fabricated profile/work data.</li>
                <li>No use of Services in violation of sanctions, export controls, or applicable labor/contracting laws.</li>
              </ul>
            </Section>

            <Section title="10. Content and Intellectual Property">
              <ul>
                <li>You retain ownership of your content, subject to the licenses granted in these Terms.</li>
                <li>
                  You grant us a worldwide, non-exclusive, royalty-free license to host, process, store, display, and use your
                  content as necessary to operate, secure, and improve the Services.
                </li>
                <li>You represent that your content does not violate any third-party rights or applicable laws.</li>
              </ul>
            </Section>

            <Section title="11. Confidentiality">
              <p>
                You must protect non-public information received through the Services and use it only for authorized business
                purposes. Confidentiality obligations survive account termination.
              </p>
            </Section>

            <Section title="12. AI-Generated Content">
              <p>
                The Services may generate profile summaries and operational suggestions using AI models based on submitted
                records. AI output is informational, may be incomplete, and must be reviewed before business-critical use.
              </p>
            </Section>

            <Section title="13. Third-Party Services">
              <p>
                The Services may integrate with third-party providers (including Stripe, Google Cloud, Firebase, and identity
                services). Your use of third-party services may be subject to separate terms and policies.
              </p>
            </Section>

            <Section title="14. Suspension and Termination">
              <ul>
                <li>We may suspend, limit, or terminate access for violations, security risk, legal obligations, or abuse.</li>
                <li>We may preserve or disclose records when required for compliance, investigations, or dispute handling.</li>
                <li>Termination does not waive obligations incurred before termination.</li>
              </ul>
            </Section>

            <Section title="15. Disclaimers">
              <p>
                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
                INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ERROR-FREE OPERATION.
              </p>
            </Section>

            <Section title="16. Limitation of Liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                PUNITIVE DAMAGES, OR LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS INTERRUPTION. OUR AGGREGATE LIABILITY SHALL
                NOT EXCEED THE FEES PAID TO US DURING THE {liabilityCapMonths} ({liabilityCapMonths}) MONTHS PRECEDING THE EVENT
                GIVING RISE TO THE CLAIM.
              </p>
            </Section>

            <Section title="17. Indemnification">
              <p>
                You agree to defend, indemnify, and hold harmless the Company and its affiliates, officers, employees, and
                agents from claims, losses, liabilities, costs, and expenses arising from your use of the Services, your
                content, or your breach of these Terms.
              </p>
            </Section>

            <Section title="18. Governing Law and Dispute Resolution">
              <p>
                These Terms are governed by the laws of {governingJurisdiction}, without regard to conflict-of-law principles.
                Any dispute shall be resolved in {venue}, except where mandatory law provides otherwise.
              </p>
              {arbitrationEnabled ? (
                <p>
                  Disputes may be resolved by binding arbitration administered by {arbitrationForum} under {arbitrationRules},
                  with class action waiver to the extent enforceable by applicable law.
                </p>
              ) : null}
            </Section>

            <Section title="19. Changes to Terms">
              <p>
                We may revise these Terms at any time. Material changes will be posted with an updated effective date.
                Continued use after the effective date constitutes acceptance.
              </p>
            </Section>

            <Section title="20. Miscellaneous">
              <ul>
                <li>Severability: invalid provisions are severed; remaining provisions remain in effect.</li>
                <li>No waiver: failure to enforce is not a waiver of rights.</li>
                <li>Assignment: you may not assign rights without prior written consent.</li>
                <li>Entire agreement: these Terms and incorporated policies constitute the complete agreement.</li>
              </ul>
            </Section>

            <Section title="21. Contact Information">
              <p>
                Questions about these Terms may be directed to {legalName} via{" "}
                <a href={`mailto:${legalEmail}`}>{legalEmail}</a>.
              </p>
              {legalAddress ? <p>Mailing address: {legalAddress}.</p> : null}
            </Section>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ToS;
